import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useTheme,
  FormHelperText,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import Swal from "sweetalert2";
import { createFinancialTransactionsForAutoAccrual } from "../../api/financialTransactions/createFinancialTransactionsForAutoAccrual";

// Ay verileri
const months = [
  { id: 1, name: "Ocak" },
  { id: 2, name: "Şubat" },
  { id: 3, name: "Mart" },
  { id: 4, name: "Nisan" },
  { id: 5, name: "Mayıs" },
  { id: 6, name: "Haziran" },
  { id: 7, name: "Temmuz" },
  { id: 8, name: "Ağustos" },
  { id: 9, name: "Eylül" },
  { id: 10, name: "Ekim" },
  { id: 11, name: "Kasım" },
  { id: 12, name: "Aralık" },
];

const AutoTransactionForAccrual = ({
  open,
  onClose,
  handleSearch,
  taxpayerId,
}) => {
  const theme = useTheme();

  const initialFormState = {
    periods: [],
    year: new Date().getFullYear(),
  };

  const [formData, setFormData] = useState(initialFormState);
  const handleClose = () => {
    setFormData(initialFormState);
    setSelectedMonths([]);
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (formData.periods.length === 0) {
      newErrors.periods = "En az bir ay seçmelisiniz";
    }

    if (!formData.year) {
      newErrors.year = "Yıl seçimi zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMonthsChange = (event, newValues) => {
    setSelectedMonths(newValues);
    setFormData({
      ...formData,
      periods: newValues.map((month) => month.id),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!validateForm()) {
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
      return;
    }

    await createFinancialTransactionsForAutoAccrual({ taxpayerId, ...formData })
      .then((res) => {
        Swal.fire({
          text: res.data.message || "Başarıyla Kaydedildi.",
          icon: "success",
          showConfirmButton: false,
        });
      })
      .catch((err) => {
        Swal.fire({
          text: err.response?.data?.message || "Hata oluştu",
          icon: "error",
          showConfirmButton: false,
        });
      });

    handleSearch();
    handleClose();
  };

  const inputStyle = {
    marginBottom: "8px",
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 12px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: "white",
          fontWeight: 500,
          padding: "6px 24px",
        }}
      >
        Ay Seçimi
      </DialogTitle>
      <DialogContent sx={{ padding: "20px", mt: "12px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl
            fullWidth
            size="small"
            sx={{ paddingTop: "8px" }}
            required
            error={submitted && !!errors.year}
          >
            <InputLabel shrink sx={{ marginTop: "9px" }}>
              Yıl
            </InputLabel>
            <Select
              name="year"
              value={formData.year}
              onChange={handleChange}
              label="Yıl"
              size="small"
            >
              {[
                new Date().getFullYear() - 2,
                new Date().getFullYear() - 1,
                new Date().getFullYear(),
                new Date().getFullYear() + 1,
              ].map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
            {submitted && errors.year && (
              <FormHelperText>{errors.year}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            error={submitted && !!errors.periods}
            sx={{ paddingTop: "8px" }}
          >
            <Autocomplete
              multiple
              options={months}
              getOptionLabel={(option) => option.name}
              value={selectedMonths}
              onChange={handleMonthsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aylar"
                  placeholder="Ay seçiniz"
                  error={submitted && !!errors.periods}
                  helperText={submitted && errors.periods}
                  required
                  sx={inputStyle}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                  />
                ))
              }
              disableCloseOnSelect
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "flex-end", gap: "12px" }}
      >
        <Button
          onClick={handleClose}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "100px",
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          startIcon={<Add />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            minWidth: "100px",
          }}
        >
          Oluştur
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoTransactionForAccrual;
